import { PendingImage } from "@components/camera-roll-image";
import { ImagesType } from "@features/images/type";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/16/solid";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

interface StudioMobilePreviewProps {
  image: ImagesType;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

let lastNavigationDirection = "";

export const StudioMobilePreview = ({
  image,
  onNavigatePrev,
  onNavigateNext,
  hasPrev = false,
  hasNext = false,
}: StudioMobilePreviewProps) => {
  const [currentScale, setCurrentScale] = useState(1);
  const x = useMotionValue(0);

  // Calculate arrow opacity based on drag distance
  const SWIPE_THRESHOLD = window.screen.width * 0.5;

  // Arrow opacity: 0 -> 0.5 (gradual), then 0.5 -> 1 (instant at threshold)
  const leftArrowOpacity = useTransform(
    x,
    [0, SWIPE_THRESHOLD * 0.5, SWIPE_THRESHOLD],
    [0, 0.5, 1]
  );
  const rightArrowOpacity = useTransform(
    x,
    [-SWIPE_THRESHOLD, -SWIPE_THRESHOLD * 0.5, 0],
    [1, 0.5, 0]
  );
  const imageOpacity = useTransform(
    x,
    [-SWIPE_THRESHOLD, 0, SWIPE_THRESHOLD],
    [hasNext ? 0 : 1, 1, hasPrev ? 0 : 1]
  );

  // Determine initial animation based on navigation direction
  const getInitialX = () => {
    if (lastNavigationDirection === "left") return -100; // Coming from right (swiped left)
    if (lastNavigationDirection === "right") return 100; // Coming from left (swiped right)
    lastNavigationDirection = "";
    return 0;
  };

  if (image.status === "pending") {
    return (
      <div className="w-screen h-available-screen-height relative">
        <PendingImage image={image} />
      </div>
    );
  }

  return (
    <div
      className="w-full relative"
      style={{
        height: "calc( 100dvh - var(--safe-area-top) )",
        marginTop: "-3rem",
      }}
    >
      <div className="absolute w-full h-full flex items-center justify-center relative overflow-hidden">
        {/* Left arrow indicator */}
        {hasPrev && (
          <motion.div
            className="absolute left-4 top-1/2 -translate-y-1/2 z-0 pointer-events-none"
            style={{ opacity: leftArrowOpacity }}
          >
            <div className="bg-black/50 rounded-full p-4">
              <ChevronLeftIcon className="h-12 w-12 text-white" />
            </div>
          </motion.div>
        )}

        {/* Right arrow indicator */}
        {hasNext && (
          <motion.div
            className="absolute right-4 top-1/2 -translate-y-1/2 z-0 pointer-events-none"
            style={{ opacity: rightArrowOpacity }}
          >
            <div className="bg-black/50 rounded-full p-4">
              <ChevronRightIcon className="h-12 w-12 text-white" />
            </div>
          </motion.div>
        )}

        {/* Zoomable and pannable image */}
        <TransformWrapper
          initialScale={1}
          minScale={1}
          maxScale={4}
          doubleClick={{ mode: "toggle", step: 1 }}
          wheel={{ disabled: false }}
          panning={{ disabled: false }}
          limitToBounds={false}
          centerOnInit={true}
          centerZoomedOut={true}
          zoomAnimation={{
            disabled: false,
          }}
          onPinchingStop={(ref) => {
            ref.resetTransform();
          }}
          onTransformed={(ref) => {
            setCurrentScale(ref.state.scale);
          }}
          onPanning={(ref) => {
            if (ref.state.scale === 1) {
              const deltaX = ref.state.positionX;
              x.set(deltaX);
            }
          }}
          onPanningStop={(ref) => {
            if (ref.state.scale === 1) {
              const offset = x.get();

              // Check if swipe threshold is met
              if (offset > SWIPE_THRESHOLD && hasPrev) {
                lastNavigationDirection = "left";
                onNavigatePrev?.();
                ref.instance.cleanupWindowEvents();
              } else if (offset < -SWIPE_THRESHOLD && hasNext) {
                lastNavigationDirection = "right";
                onNavigateNext?.();
                ref.instance.cleanupWindowEvents();
              }

              // Reset position
              x.set(0);
            }
          }}
        >
          {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
            <motion.div
              initial={{ x: getInitialX(), opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                opacity: { duration: 0.2 },
              }}
              className="w-full h-full"
            >
              <TransformComponent
                wrapperClass="!w-full !h-full"
                contentClass="!flex !items-center !justify-center !w-full !h-full"
              >
                <motion.div
                  className="flex items-center justify-center w-full"
                  style={{
                    opacity: imageOpacity,
                  }}
                >
                  {image?.type !== "video" && (
                    <img
                      src={image.url}
                      alt={image.id}
                      className="max-w-full max-h-[calc(100vh-8rem)] w-auto h-auto select-none"
                      draggable={false}
                    />
                  )}
                  {image?.type === "video" && (
                    <video
                      src={image.url}
                      controls
                      className="max-w-full max-h-[calc(100vh-8rem)] w-auto h-auto"
                      draggable={false}
                      playsInline
                    />
                  )}
                </motion.div>
              </TransformComponent>
            </motion.div>
          )}
        </TransformWrapper>

        {/* Zoom indicator */}
        {currentScale > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm z-20">
            {Math.round(currentScale * 100)}%
          </div>
        )}
      </div>
    </div>
  );
};
